import { loadFromLocalStorage } from './local-storage';

/**
 * Empresa da tela, pelo host do navegador.
 * https://admin.hidrozap.com.br → 1
 * https://conlig.com.br → 2
 * Demais hosts (localhost, api-dev) permanecem Hidrozap (1),
 * salvo override de teste em localStorage na chave `EmpresaId` (1 ou 2).
 * localStorage.setItem('EmpresaId', '2') no console do navegador.
 */
export const resolveEmpresaId = (hostname?: string): number => {
  const host = (hostname ?? readHostname()).trim().toLowerCase();
  const fromHost = isConligHost(host) ? 2 : 1;
  if (isProductionHost(host)) {
    return fromHost;
  }

  const manual = readManualEmpresaId();
  if (manual === 1 || manual === 2) {
    return manual;
  }
  return fromHost;
};

export const resolveAdminTitle = (hostname?: string): string => {
  return resolveEmpresaId(hostname) === 2 ? 'Conlig Admin' : 'HidroZap Admin';
};

export const resolveAdminFavicon = (hostname?: string): string => {
  return resolveEmpresaId(hostname) === 2 ? '/conlig.ico' : '/favicon.ico';
};

const isConligHost = (host: string): boolean => {
  return host === 'conlig.com.br' || host.endsWith('.conlig.com.br');
};

const isProductionHost = (host: string): boolean => {
  return host === 'admin.hidrozap.com.br' || isConligHost(host);
};

const readManualEmpresaId = (): number | null => {
  try {
    const value = loadFromLocalStorage('EmpresaId');
    const parsed = typeof value === 'string' ? Number(value) : value;
    if (parsed === 1 || parsed === 2) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * Representação selecionada em FiltroRepresentacao (Layout).
 * O filtro grava o id em localStorage pela chave `representacao`.
 * 0 = "Todos".
 */
export const resolveRepresentacaoId = (): number => {
  const value = loadFromLocalStorage('representacao');
  const parsed = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(parsed) ? Number(parsed) : 0;
};

const LIST_GET_TEMPLATES = [
  '/AgendaRota/full/{idUsuario}',
  '/AgendaRota/rota/{idRota}',
  '/CatalogoProduto',
  '/Cliente',
  '/Cliente/email/{email}',
  '/Cliente/fonte-lead/{idFonteLead}',
  '/Cliente/nome/{nome}',
  '/Cliente/telefone/{telefone}',
  '/Cliente/{idRepresentacao}',
  '/Cliente/{idRepresentacao}/email/{email}',
  '/Cliente/{idRepresentacao}/fonte-lead/{idFonteLead}',
  '/Cliente/{idRepresentacao}/nome/{nome}',
  '/Cliente/{idRepresentacao}/telefone/{telefone}',
  '/Concentrador',
  '/ControleMensal/rota/{rotaId}/{ano}/{mes}/anexos',
  '/ControleMensal/{ano}/{mes}',
  '/Dominios/catalogo-produtos',
  '/Dominios/etapas-proposta',
  '/Dominios/fontes-lead',
  '/Dominios/formas-pagamento',
  '/Dominios/preferencias-contato',
  '/Dominios/status-proposta',
  '/Dominios/titulos-contato',
  '/Email/envios/pix-cobranca/{pixCobrancaId}',
  '/Email/remetentes',
  '/Email/residencias-envio/{rotaId}',
  '/Email/templates',
  '/Faixa',
  '/FonteLead',
  '/FormaPagamento',
  '/GrupoEndereco/simplificado/{idRota}',
  '/GrupoEndereco/{idRota}',
  '/Hidrometro/bloco/{addressGroupId}',
  '/Hidrometro/medidores/{idRota}',
  '/Hidrometro/medidores/{idRota}/{idGrupoEndereco}',
  '/Hidrometro/{idResidencia}',
  '/HidrometroLeitura/altoConsumo/reprocessar/{ano}/{mes}',
  '/HidrometroLeitura/exportarExcel/{idRota}/{anoMesReferencia}',
  '/HidrometroLeitura/exportarExcelCond/{idRota}/{anoMesReferencia}',
  '/HidrometroLeitura/exportarLeituraAtual/{idRota}/{anoMesReferencia}',
  '/HidrometroLeitura/leituraDetalhes/{idResidencia}/{anoMesReferencia}',
  '/HidrometroLeitura/leituraDetalhesByAgendaRotaId/{idHidrometer}/{agendaRotaId}',
  '/HidrometroLeitura/leituraDetalhesPorRota/{idRota}/{anoMesReferencia}',
  '/HidrometroLeitura/leituras/{idRota}/{anoMesReferencia}',
  '/HidrometroLeitura/medidores/{idRota}/{anoMesReferencia}',
  '/HidrometroLeitura/medidoresLeiturasAgendaRota/{idAgendaRota}',
  '/HidrometroLeitura/medidoresLeiturasAgendaRotaTemplate/{idAgendaRota}',
  '/Ibge',
  '/InterPixWebhook',
  '/ItemGestaoMensal/proposta/{propostaId}',
  '/ItemImplantacao/proposta/{propostaId}',
  '/Leitura/unitario',
  '/LeituraRemota/consumo',
  '/LeituraRemota/{idRepresentacao}/{idRegiao}/{anoMesReferencia}',
  '/MotivoIlegivel',
  '/Orcamento/proposta/{propostaId}',
  '/OrdemServico/abertas',
  '/OrdemServico/finalizadas-ou-canceladas',
  '/PixCobranca',
  '/PreferenciaContato',
  '/Proposta',
  '/Proposta/data-atualizacao',
  '/Proposta/proximo-contato',
  '/Proposta/{idRepresentacao}',
  '/Proposta/{idRepresentacao}/data-atualizacao',
  '/PropostaAlteracao/proposta/{propostaId}',
  '/PropostaEtapa',
  '/PropostaFoto/proposta/{idProposta}',
  '/PropostaLinks/proposta/{idProposta}',
  '/PropostaStatus',
  '/Regiao',
  '/RelatorioCusto/demonstrativos',
  '/RelatorioCusto/demonstrativos/{rotaId}',
  '/Representacao',
  '/Residencia/byroute{idRoute}',
  '/Residencia/id/{idResidencia}',
  '/Residencia/{idGrupoEndereco}',
  '/RotaAlerta/rota/{rotaId}',
  '/RotaAlerta/{ano}/{mes}',
  '/RotaFechamento',
  '/RotaFechamento/ano/{anoReferencia}/mes/{mesReferencia}',
  '/RotaFechamento/representacao/{idRepresentacao}/regiao/{idRegiao}/ano/{anoReferencia}/mes/{mesReferencia}',
  '/RotaFechamento/rota/{rotaId}',
  '/Rotas',
  '/Rotas/AgendaRota/{anoMesReferencia}/{idRepresentacao}/{idRegiao}',
  '/Rotas/Condomino/{idRepresentacao}/{idRegiao}',
  '/Rotas/sincronizado/{idRepresentacao}/{idRegiao}',
  '/Rotas/{idRepresentacao}/{idRegiao}',
  '/StatusProposta/proposta/{propostaId}',
  '/TabelaFaixa/faixa/{idFaixa}',
  '/TabelaFaixaGas/faixa/{idFaixa}',
  '/Usuario',
  '/Usuario/Leituristas/{idRepresentacao}/{idRegiao}',
  '/Usuario/Representacoes/{idUsuario}',
  '/Usuario/Rotas/{idUsuario}',
  '/Usuario/perfis',
  '/Usuario/{idUsuario}/perfis',
];

/** POST de criação cujo request no swagger inclui EmpresaId e RepresentacaoId. */
const CREATE_POST_PATHS = new Set([
  '/agendarota',
  '/catalogoproduto',
  '/cliente',
  '/concentrador',
  '/dominios/catalogo-produtos',
  '/dominios/etapas-proposta',
  '/dominios/fontes-lead',
  '/dominios/formas-pagamento',
  '/dominios/preferencias-contato',
  '/dominios/status-proposta',
  '/dominios/titulos-contato',
  '/email/templates',
  '/faixa',
  '/fontelead',
  '/formapagamento',
  '/grupoendereco',
  '/hidrometro',
  '/interpixwebhook',
  '/motivoilegivel',
  '/orcamento',
  '/ordemservico',
  '/pixcobranca',
  '/pixcobranca/gerar-recorrente',
  '/preferenciacontato',
  '/proposta',
  '/propostaetapa',
  '/propostastatus',
  '/regiao',
  '/residencia',
  '/tabelafaixa',
  '/tabelafaixagas',
]);

const QUERY_POST_TEMPLATES = [
  '/ControleMensal/rota/{rotaId}/{ano}/{mes}/anexos',
  '/LeituraRemota/importar',
  '/LeituraRemota/importar-smc',
];

/** GETs que casam com um template de lista, mas não são listagem. */
const LIST_GET_EXCLUSIONS = [
  '/Rotas/id/{idRota}',
  '/Rotas/ListarUnidadesIntegracao/{idRota}',
  '/Residencia/generateresidencepin',
];

/**
 * Representação e região já vão no path.
 * A query leva somente EmpresaId.
 */
const EMPRESA_ONLY_QUERY_TEMPLATES = [
  '/Rotas/AgendaRota/{anoMesReferencia}/{idRepresentacao}/{idRegiao}',
];

export const applyApiContexto = (
  method: string,
  endpoint: string,
  data: unknown,
): { endpoint: string; data: unknown } => {
  const path = normalizeApiPath(endpoint);
  if (!path) {
    return { endpoint, data };
  }

  const verb = method.toLowerCase();
  if (verb === 'get' && matchesAny(listGetPatterns, path) && !matchesAny(listGetExclusions, path)) {
    const includeRepresentacao = !matchesAny(empresaOnlyQueryPatterns, path);
    return { endpoint: withContextoQuery(endpoint, includeRepresentacao), data };
  }

  if (verb === 'post' && CREATE_POST_PATHS.has(path.toLowerCase())) {
    return { endpoint, data: withContextoBody(data) };
  }

  if (verb === 'post' && matchesAny(queryPostPatterns, path)) {
    return { endpoint: withContextoQuery(endpoint), data };
  }

  return { endpoint, data };
};

const readHostname = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname || '';
};

const templateToRegExp = (template: string): RegExp => {
  const source = template
    .split('/')
    .map((segment) => {
      if (!segment) {
        return '';
      }
      return segment
        .replace(/\{[^}]+\}/g, '\u0000')
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\u0000/g, '[^/]+');
    })
    .join('/');
  return new RegExp(`^${source}$`, 'i');
};

const listGetPatterns = LIST_GET_TEMPLATES.map(templateToRegExp);
const listGetExclusions = LIST_GET_EXCLUSIONS.map(templateToRegExp);
const empresaOnlyQueryPatterns = EMPRESA_ONLY_QUERY_TEMPLATES.map(templateToRegExp);
const queryPostPatterns = QUERY_POST_TEMPLATES.map(templateToRegExp);

const matchesAny = (patterns: RegExp[], path: string): boolean => {
  return patterns.some((pattern) => pattern.test(path));
};

const normalizeApiPath = (endpoint: string): string | null => {
  const trimmed = (endpoint || '').trim();
  if (!trimmed) {
    return null;
  }

  let path = trimmed;
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return null;
    }
  }

  const queryIndex = path.indexOf('?');
  if (queryIndex >= 0) {
    path = path.slice(0, queryIndex);
  }

  if (path.toLowerCase().startsWith('/api/')) {
    path = path.slice(4);
  }

  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  return path;
};

const withContextoQuery = (endpoint: string, includeRepresentacao = true): string => {
  const queryIndex = endpoint.indexOf('?');
  const path = queryIndex >= 0 ? endpoint.slice(0, queryIndex) : endpoint;
  const search = new URLSearchParams(queryIndex >= 0 ? endpoint.slice(queryIndex + 1) : '');
  setParam(search, 'EmpresaId', String(resolveEmpresaId()));
  if (includeRepresentacao) {
    setParam(search, 'RepresentacaoId', String(resolveRepresentacaoId()));
  } else {
    removeParam(search, 'RepresentacaoId');
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

const setParam = (search: URLSearchParams, name: string, value: string) => {
  removeParam(search, name);
  search.append(name, value);
};

const removeParam = (search: URLSearchParams, name: string) => {
  const keys = Array.from(search.keys());
  keys.forEach((key) => {
    if (key.toLowerCase() === name.toLowerCase()) {
      search.delete(key);
    }
  });
};

const withContextoBody = (data: unknown): unknown => {
  const contexto = {
    EmpresaId: resolveEmpresaId(),
    RepresentacaoId: resolveRepresentacaoId(),
  };

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    data.set('EmpresaId', String(contexto.EmpresaId));
    data.set('RepresentacaoId', String(contexto.RepresentacaoId));
    return data;
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return { ...(data as Record<string, unknown>), ...contexto };
  }

  return contexto;
};
